import React, { useMemo, useState } from "react";
import styles from "./FilterSidebar.module.css";
import { FiChevronUp, FiChevronDown, FiSearch } from "react-icons/fi";

export interface Option {
  id: string;
  name: string;
  count?: number;
  icon?: React.ReactNode;
  thumbnail?: string;
  activeThumbnail?: string;
}

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={styles.section}>
      <button className={styles.sectionHeader} onClick={() => setOpen(!open)}>
        <span>{title}</span>
        {open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
      </button>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </div>
  );
};

/** Small reusable checkbox list used by Brand / Tyre Type / Tyre Position */
interface CheckboxGroupProps {
  options: Option[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  searchable?: boolean;
  visibleCount?: number;
  onViewMore?: () => void;
  loading?: boolean;
  emptyLabel?: string;
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  selectedIds,
  onToggle,
  searchable = false,
  visibleCount,
  onViewMore,
  loading = false,
  emptyLabel = "No options found",
}) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search, searchable]);

  const visible =
    typeof visibleCount === "number"
      ? filtered.slice(0, visibleCount)
      : filtered;

  if (loading) {
    return <div className={styles.loadingText}>Loading...</div>;
  }

  return (
    <>
      {searchable && (
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} size={14} />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      )}

      <div className={styles.checkboxList}>
        {visible.length === 0 && (
          <div className={styles.emptyText}>{emptyLabel}</div>
        )}
        {visible.map((opt) => (
          <label key={opt.id} className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={selectedIds.includes(opt.id)}
              onChange={() => onToggle(opt.id)}
              className={styles.checkbox}
            />
            <span className={styles.checkboxText}>
              {opt.name}
              {typeof opt.count === "number" && (
                <span className={styles.count}> ({opt.count})</span>
              )}
            </span>
          </label>
        ))}
      </div>

      {typeof visibleCount === "number" &&
        filtered.length > visibleCount &&
        onViewMore && (
          <button className={styles.viewMore} onClick={onViewMore}>
            VIEW MORE
          </button>
        )}
    </>
  );
};

interface DependentDropdownProps {
  placeholder: string;
  options: Option[];
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}

const DependentDropdown: React.FC<DependentDropdownProps> = ({
  placeholder,
  options,
  value,
  onChange,
  disabled,
}) => (
  <select
    className={styles.select}
    value={value ?? ""}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value || null)}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.id} value={o.id}>
        {o?.name?.toUpperCase()}
      </option>
    ))}
  </select>
);

export interface FilterSidebarProps {
  isOpen: boolean;
  onClose: () => void;

  /** VEHICLE TYPE (2W/4W/Truck) — comes from predefined "CATEGORY" API */
  vehicleTypes: Option[];
  selectedVehicleType: string | null;
  onVehicleTypeChange: (id: string) => void;

  /** VEHICLE DETAILS — dependent Make -> Model -> Variant dropdowns */
  makes: Option[];
  models: Option[];
  variants: Option[];
  selectedMake: string | null;
  selectedModel: string | null;
  selectedVariant: string | null;
  onMakeChange: (id: string | null) => void;
  onModelChange: (id: string | null) => void;
  onVariantChange: (id: string | null) => void;
  showVariant?: boolean; // e.g. only for TYRE category

  /** TYRE BRAND */
  brands: Option[];
  selectedBrandIds: string[];
  onBrandToggle: (id: string) => void;
  visibleBrandsCount: number;
  onViewMoreBrands: () => void;
  brandsLoading?: boolean;

  /** TYRE SIZES (Width / Aspect / Rim) */
  showTyreSizes?: boolean;
  widthOptions: Option[];
  aspectOptions: Option[];
  rimOptions: Option[];
  selectedWidth: string | null;
  selectedAspect: string | null;
  selectedRim: string | null;
  onWidthChange: (id: string | null) => void;
  onAspectChange: (id: string | null) => void;
  onRimChange: (id: string | null) => void;

  /** PRICE RANGE */
  priceMin?: number;
  priceMax?: number;
  priceValue: number;
  onPriceChange: (value: number) => void;

  /** TYRE TYPE (Tubeless / With Tube) */
  showTyreType?: boolean;
  tyreTypeOptions: Option[];
  selectedTyreTypeIds: string[];
  onTyreTypeToggle: (id: string) => void;

  /** TYRE POSITION (Front / Rear) */
  showTyrePosition?: boolean;
  tyrePositionOptions: Option[];
  selectedTyrePositionIds: string[];
  onTyrePositionToggle: (id: string) => void;

  onResetAll: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  isOpen,
  onClose,

  vehicleTypes,
  selectedVehicleType,
  onVehicleTypeChange,

  makes,
  models,
  variants,
  selectedMake,
  selectedModel,
  selectedVariant,
  onMakeChange,
  onModelChange,
  onVariantChange,
  showVariant = true,

  brands,
  selectedBrandIds,
  onBrandToggle,
  visibleBrandsCount,
  onViewMoreBrands,
  brandsLoading,

  showTyreSizes = true,
  widthOptions,
  aspectOptions,
  rimOptions,
  selectedWidth,
  selectedAspect,
  selectedRim,
  onWidthChange,
  onAspectChange,
  onRimChange,

  priceMin = 0,
  priceMax = 25000,
  priceValue,
  onPriceChange,

  showTyreType = true,
  tyreTypeOptions,
  selectedTyreTypeIds,
  onTyreTypeToggle,

  showTyrePosition = true,
  tyrePositionOptions,
  selectedTyrePositionIds,
  onTyrePositionToggle,

  onResetAll,
}) => {
  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={onClose} />}
      <aside
        className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ""}`}
      >
        <div className={styles.mobileHeader}>
          <span className={styles.mobileTitle}>Filters</span>
          <button className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Vehicle Type */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>VEHICLE TYPE</p>
          <div className={styles.vehicleList}>
            {vehicleTypes.map((v) => {
              const isActive = selectedVehicleType === v.id;
              const imgSrc = isActive
                ? v.activeThumbnail || v.thumbnail
                : v.thumbnail || v.activeThumbnail;

              return (
                <button
                  key={v.id}
                  className={`${styles.vehicleBtn} ${
                    isActive ? styles.vehicleBtnActive : ""
                  }`}
                  onClick={() => onVehicleTypeChange(v.id)}
                >
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={v.name}
                      className={styles.vehicleImage}
                    />
                  ) : (
                    v.icon && (
                      <span className={styles.vehicleIcon}>{v.icon}</span>
                    )
                  )}
                  {v.name}
                </button>
              );
            })}
          </div>
        </div>
        {/* Vehicle Details */}
        <CollapsibleSection title="VEHICLE DETAILS">
          <DependentDropdown
            placeholder="Select Make"
            options={makes}
            value={selectedMake}
            onChange={onMakeChange}
          />
          <DependentDropdown
            placeholder="Select Model"
            options={models}
            value={selectedModel}
            onChange={onModelChange}
            disabled={!selectedMake}
          />
          {showVariant && (
            <DependentDropdown
              placeholder="Select Variant"
              options={variants}
              value={selectedVariant}
              onChange={onVariantChange}
              disabled={!selectedModel}
            />
          )}
        </CollapsibleSection>

        {/* Tyre Brand */}
        <CollapsibleSection title="TYRE BRAND">
          <CheckboxGroup
            options={brands}
            selectedIds={selectedBrandIds}
            onToggle={onBrandToggle}
            searchable
            visibleCount={visibleBrandsCount}
            onViewMore={onViewMoreBrands}
            loading={brandsLoading}
            emptyLabel="No brands found"
          />
        </CollapsibleSection>

        {/* Tyre Sizes */}
        {showTyreSizes && (
          <CollapsibleSection title="TYRE SIZES">
            <DependentDropdown
              placeholder="Width"
              options={widthOptions}
              value={selectedWidth}
              onChange={onWidthChange}
            />
            <DependentDropdown
              placeholder="Aspect"
              options={aspectOptions}
              value={selectedAspect}
              onChange={onAspectChange}
            />
            <DependentDropdown
              placeholder="Rim"
              options={rimOptions}
              value={selectedRim}
              onChange={onRimChange}
            />
          </CollapsibleSection>
        )}

        {/* Price Range */}
        <CollapsibleSection title="PRICE RANGE">
          <input
            type="range"
            min={priceMin}
            max={priceMax}
            value={priceValue}
            onChange={(e) => onPriceChange(Number(e.target.value))}
            className={styles.rangeSlider}
          />
          <div className={styles.priceLabels}>
            <span>₹{priceMin.toLocaleString("en-IN")}</span>
            <span>₹{priceValue.toLocaleString("en-IN")}</span>
          </div>
        </CollapsibleSection>

        {/* Tyre Type */}
        {showTyreType && (
          <CollapsibleSection title="TYRE TYPE">
            <CheckboxGroup
              options={tyreTypeOptions}
              selectedIds={selectedTyreTypeIds}
              onToggle={onTyreTypeToggle}
              emptyLabel="No tyre types found"
            />
          </CollapsibleSection>
        )}

        {/* Tyre Position */}
        {showTyrePosition && (
          <CollapsibleSection title="TYRE POSITION">
            <CheckboxGroup
              options={tyrePositionOptions}
              selectedIds={selectedTyrePositionIds}
              onToggle={onTyrePositionToggle}
              emptyLabel="No positions found"
            />
          </CollapsibleSection>
        )}

        <button className={styles.resetBtn} onClick={onResetAll}>
          RESET ALL FILTERS
        </button>
      </aside>
    </>
  );
};

export default FilterSidebar;
